<template>
  <div>
    <p class="lead d-flex justify-content-between">
      <span>Validar eventos del dispositivo</span>
      <span class="small">{{ action.data.timestamp }}</span>
    </p>

    <div class="cmd-card">
      <div class="cmd-card__header">
        <span class="cmd-card__icon">
          <feather-icon icon="PlayCircleIcon" size="16"/>
        </span>
        <span class="cmd-card__name">Validar eventos del dispositivo</span>

        <b-button
          v-if="!executed"
          variant="primary"
          size="sm"
          :disabled="terminated || isExecuting"
          @click="onExecuteCmdClick(action)"
        >
          <b-spinner v-if="isExecuting" small class="mr-25"/>
          <feather-icon v-else icon="PlayCircleIcon" size="18" class="mr-25"/>
          {{ isExecuting ? "Validando..." : $t("action_plan.viewer.execute") }}
        </b-button>

        <span
          v-if="executed"
          class="cmd-card__status"
          :class="statusClass"
        >
          <feather-icon :icon="statusIcon" size="14" class="mr-25"/>
          {{ statusLabel }}
        </span>
      </div>

      <div v-if="executed" class="cmd-card__body">
        <p v-if="message" class="mb-1" :class="messageClass">
          {{ message }}
        </p>

        <div v-if="hasResult" class="validation-summary">
          <div class="validation-summary__item">
            <span class="validation-summary__label">Sucursal</span>
            <strong>{{ facilityName }}</strong>
            <div v-if="facilityIndicators.length" class="facility-indicators">
              <b-badge
                v-for="indicator in facilityIndicators"
                :key="indicator.key"
                :variant="indicator.variant"
              >
                {{ indicator.label }}
              </b-badge>
            </div>
          </div>
          <div class="validation-summary__item">
            <span class="validation-summary__label">Eventos detectados</span>
            <strong>{{ eventsCount }}</strong>
          </div>
          <div class="validation-summary__item">
            <span class="validation-summary__label">Origen de la validación</span>
            <strong>{{ originDescription }}</strong>
          </div>
        </div>

        <section v-if="hasResult" class="result-section">
          <div class="result-section__title">
            <span>Dispositivos con eventos</span>
            <b-badge variant="light-primary">{{ eventsCount }}</b-badge>
          </div>

          <div v-if="events.length" class="table-responsive">
            <table class="table table-sm mb-0 result-table">
              <thead>
              <tr>
                <th class="text-center">Cantidad</th>
                <th>Tipo</th>
                <th>Dispositivo</th>
                <th>Panel DMP</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(event, index) in events" :key="eventKey(event, index)">
                <td class="text-center font-weight-bold">{{ event.count }}</td>
                <td>{{ event.device.type || "No identificado" }}</td>
                <td>{{ event.device.name || "Dispositivo no identificado" }}</td>
                <td>{{ dmpPanelName(event) }}</td>
              </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="empty-state mb-0">
            No se encontraron eventos pendientes en los últimos 30 días.
          </p>
        </section>

        <section v-if="hasResult" class="result-section">
          <div class="result-section__title">
            <span>Últimos mantenimientos de la sucursal</span>
            <b-badge variant="light-secondary">{{ maintenances.length }}</b-badge>
          </div>

          <div v-if="maintenances.length" class="table-responsive">
            <table class="table table-sm mb-0 result-table">
              <thead>
              <tr>
                <th>Referencia</th>
                <th>Fecha (hora centro)</th>
              </tr>
              </thead>
              <tbody>
              <tr
                v-for="maintenance in maintenances"
                :key="`${maintenance.reference}-${maintenance.created_at}`"
              >
                <td class="font-weight-bold">{{ maintenance.reference }}</td>
                <td>{{ formatCentralDate(maintenance.created_at) }}</td>
              </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="empty-state mb-0">
            No se encontraron mantenimientos en los últimos 30 días.
          </p>
        </section>

        <p v-if="status === 'released'" class="text-success mt-1 mb-0">
          Liberado por: {{ supervisor }}
        </p>
        <p v-if="status === 'released'" class="text-muted mb-0">
          {{ timestamp }}
        </p>
      </div>
    </div>

    <div class="my-1">
      <label :for="`input-${action.id}`">
        {{ $t("action_plan.viewer.comments") }}
      </label>
      <input
        :id="`input-${action.id}`"
        v-model="action.data.comments"
        type="text"
        class="form-control"
        :placeholder="$t('action_plan.viewer.comments')"
        :disabled="terminated || validating || isExecuting"
      />
    </div>

    <div class="my-1">
      <files
        v-model="action.data.files"
        :action-plan-log-uuid="actionPlanLogUuid"
        :validating="validating"
        :preview="preview"
        :terminated="terminated"
        :action="action"
        @file-added="$emit('checkpoint')"
      />
    </div>

    <div class="text-right mb-1">
      <b-button
        v-if="showContinueButton"
        :variant="isUnlocking ? 'success' : 'primary'"
        size="sm"
        :disabled="!executed || validating || isExecuting || isBlocked"
        @click="onContinueClick(action)"
      >
        {{ continueButtonLabel }}
        <feather-icon icon="ChevronRightIcon" size="18"/>
      </b-button>
    </div>
  </div>
</template>

<script>
import commandStackHelperMixin from "@/mixins/commandStackHelperMixin";
import axios from "@axios";
import store from "@/store";
import moment from "moment-timezone";
import Files from "./Files.vue";

const CENTRAL_TIMEZONE = "America/Mexico_City";

export default {
  components: {Files},
  mixins: [commandStackHelperMixin],
  props: {
    actionPlanLogUuid: String,
    alarmId: [String, Number],
    action: Object,
    terminated: Boolean,
    preview: Boolean,
    validating: Boolean,
  },
  data() {
    return {
      isExecuting: false,
    };
  },
  computed: {
    user() {
      return store.getters["user/getUser"];
    },
    result() {
      return this.action.data.data ?? {};
    },
    status() {
      return this.action.data.status;
    },
    executed() {
      return Boolean(this.action.data.executed);
    },
    events() {
      return Array.isArray(this.result.events) ? this.result.events : [];
    },
    maintenances() {
      return Array.isArray(this.result.maintenances)
        ? this.result.maintenances
        : [];
    },
    eventsCount() {
      return Number(this.result.events_count ?? 0);
    },
    facilityName() {
      return this.result.facility_name || "No identificada";
    },
    facilityIndicators() {
      const status = this.result.facility_status ?? {};
      const indicators = [];

      if (status.in_project) {
        indicators.push({
          key: "in_project",
          label: "En proyecto",
          variant: "warning",
        });
      }

      if (status.under_maintenance) {
        indicators.push({
          key: "under_maintenance",
          label: "Personal externo",
          variant: "info",
        });
      }

      return indicators;
    },
    originDescription() {
      const panel = this.result.panel_name || "Panel no identificado";
      const zone = this.result.zone?.name || this.result.zone?.id || "Dispositivo no identificado";

      return `${panel} · ${zone}`;
    },
    hasResult() {
      return Object.prototype.hasOwnProperty.call(this.result, "events_count");
    },
    canRelease() {
      return this.$can("allow_to_unlock_zone_validation_command", "ActionPlan");
    },
    isUnlocking() {
      return this.status === "blocked" && this.canRelease;
    },
    showContinueButton() {
      if (this.action.data.continue || this.terminated) return false;

      return this.status !== "blocked" || this.canRelease;
    },
    continueButtonLabel() {
      return this.isUnlocking
        ? "Desbloquear y continuar"
        : this.$t("action_plan.viewer.continue");
    },
    supervisor() {
      return this.result.supervisor || "";
    },
    message() {
      return this.result.message || "";
    },
    timestamp() {
      return this.result.timestamp || "";
    },
    isBlocked() {
      if (["ERROR"].includes(this.status)) return true;
      //if (["failed", "ERROR"].includes(this.status)) return true;

      return this.status === "blocked" && !this.canRelease;
    },
    statusClass() {
      if (["clear", "released", "OK"].includes(this.status)) {
        return "cmd-card__status--success";
      }

      if (this.status === "blocked") {
        return "cmd-card__status--warning";
      }

      return "cmd-card__status--error";
    },
    statusIcon() {
      if (["clear", "released", "OK"].includes(this.status)) {
        return "CheckCircleIcon";
      }

      return this.status === "blocked" ? "AlertTriangleIcon" : "XCircleIcon";
    },
    statusLabel() {
      const labels = {
        clear: "Sin eventos pendientes",
        blocked: "Revisión requerida",
        failed: "No fue posible validar",
        released: "Validado",
        OK: this.$t("action_plan.viewer.executed_success"),
        ERROR: this.$t("action_plan.viewer.executed_error"),
      };

      return labels[this.status] || this.$t("action_plan.viewer.executed_error");
    },
    messageClass() {
      if (this.status === "blocked") return "text-danger";
      if (["failed", "ERROR"].includes(this.status)) return "text-danger";

      return "text-success";
    },
  },
  methods: {
    onContinueClick(action) {
      if (this.status === "blocked" && this.canRelease) {
        this.$set(action.data, "status", "released");
        this.$set(action.data, "id_supervisor", this.user.id);
        const supervisor = `${this.user.user} | ${this.user.full_name}`;
        const timestamp = moment()
          .tz(CENTRAL_TIMEZONE)
          .format("YYYY-MM-DD HH:mm:ss");

        this.$set(action.data, "data", {
          ...this.result,
          supervisor,
          timestamp,
        });
      }

      this.$emit("continue", action);
    },
    async onExecuteCmdClick(action) {
      if (this.isExecuting) return;

      if (this.preview) {
        this.$set(action.data, "executed", true);
        this.$set(action.data, "status", "clear");
        this.$set(action.data, "data", {
          status: "clear",
          can_continue: true,
          message: "No se encontraron eventos pendientes.",
          events_count: 0,
          facility_name: "Sucursal de ejemplo",
          facility_status: {
            in_project: true,
            under_maintenance: true,
          },
          panel_name: "Panel de ejemplo",
          zone: {id: "-", name: "Zona de ejemplo"},
          events: [],
          maintenances: [],
        });
        return;
      }

      this.isExecuting = true;
      const payload = {
        action_plan_log_id: this.actionPlanLogUuid,
        id_alarm: this.alarmId,
      };

      try {
        const {data: response} = await axios.post(
          "/v1/form-actions/dmp-zone-events.validate",
          {payload},
        );
        const operation = response?.data ?? {};

        if (operation.status === "failed") {
          this.$set(action.data, "status", "failed");
          this.$set(action.data, "data", {
            message: operation.message || "No fue posible realizar la validación.",
          });
        } else {
          const result = operation.result ?? {};
          this.$set(action.data, "status", result.status || "failed");
          this.$set(action.data, "data", result);
        }

        this.$set(action.data, "executed", true);
        this.$emit("checkpoint");
      } catch (error) {
        const message = error.response?.data?.message
          || "No fue posible realizar la validación.";

        this.$set(action.data, "executed", true);
        this.$set(action.data, "status", "failed");
        this.$set(action.data, "data", {message});
        this.$emit("checkpoint");
      } finally {
        this.isExecuting = false;
      }
    },
    dmpPanelName(event) {
      return event.device?.source === "dmp"
        ? event.device.panel_name || "Panel no identificado"
        : "—";
    },
    eventKey(event, index) {
      const device = event.device ?? {};

      return `${device.source || "unknown"}-${device.type || "unknown"}-${device.name || index}`;
    },
    formatCentralDate(value) {
      if (!value) return "—";

      const date = moment(value).tz(CENTRAL_TIMEZONE);

      return date.isValid() ? date.format("DD/MM/YYYY HH:mm") : value;
    },
  },
};
</script>

<style scoped>
.cmd-card {
  margin-bottom: 0.5rem;
  padding: 0.65rem 0.9rem;
  background-color: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 0.5rem;
}

.cmd-card__header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
}

.cmd-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: rgba(27, 61, 94, 0.08);
  color: #1b3d5e;
}

.cmd-card__name {
  flex: 1 1 auto;
  min-width: 120px;
  font-weight: 600;
  color: #5e5873;
}

.cmd-card__status {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 0.3rem 0.75rem;
  border-radius: 2rem;
  font-size: 0.8rem;
  font-weight: 500;
  line-height: 1.4;
}

.cmd-card__status--success {
  background-color: rgba(40, 199, 111, 0.12);
  color: #1f9d57;
}

.cmd-card__status--warning {
  background-color: rgba(255, 159, 67, 0.14);
  color: #d97706;
}

.cmd-card__status--error {
  background-color: rgba(234, 84, 85, 0.12);
  color: #d43b3c;
}

.cmd-card__body {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #ebe9f1;
  color: #6e6b7b;
}

.validation-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 0.65rem;
  margin-bottom: 0.85rem;
}

.validation-summary__item {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 0.65rem 0.75rem;
  border: 1px solid #ebe9f1;
  border-radius: 0.4rem;
  background-color: #fafafa;
}

.validation-summary__label {
  margin-bottom: 0.2rem;
  color: #82868b;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.025rem;
}

.facility-indicators {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-top: 0.35rem;
}

.result-section {
  margin-top: 0.85rem;
}

.result-section__title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.45rem;
  font-weight: 600;
  color: #5e5873;
}

.result-table {
  border: 1px solid #ebe9f1;
}

.result-table th {
  white-space: nowrap;
  background-color: #f8f8f8;
  color: #5e5873;
  font-size: 0.78rem;
}

.result-table td {
  vertical-align: middle;
}

.empty-state {
  padding: 0.7rem;
  border: 1px dashed #d8d6de;
  border-radius: 0.4rem;
  color: #82868b;
  background-color: #fafafa;
}

.dark-layout .cmd-card {
  background-color: rgba(255, 255, 255, 0.03);
  border-color: rgba(255, 255, 255, 0.08);
}

.dark-layout .cmd-card__body,
.dark-layout .validation-summary__item,
.dark-layout .result-table,
.dark-layout .empty-state {
  border-color: rgba(255, 255, 255, 0.08);
}

.dark-layout .validation-summary__item,
.dark-layout .result-table th,
.dark-layout .empty-state {
  background-color: rgba(255, 255, 255, 0.03);
}

.dark-layout .cmd-card__name,
.dark-layout .result-section__title,
.dark-layout .result-table th {
  color: #d0d2d6;
}

.dark-layout .cmd-card__body,
.dark-layout .empty-state {
  color: #b4b7bd;
}
</style>
